import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Form } from "@base-ui/react/form";
import { Field } from "@base-ui/react/field";
import { EntityStatus } from "@repo/types/enums";
import type { AddUpdateKeyValuePairViewModel } from "../../../types/viewModels/AddUpdateKeyValuePairViewModel";
import type { KeyValueItem, KeyValueBase } from "@repo/types/ui/keyValuePairs";
import { useAddUpdateKeyValuePairs } from "../../../hooks/queries/useKeyValuePairs";
import Button from "../button/Button";

type FormValues = {
  items: KeyValueItem[];
};

interface KeyValueMenuEditProps extends KeyValueBase {
  items: KeyValueItem[] | undefined;
  enumType?: number;
  onClose: () => void;
}

export function KeyValueMenuEdit({
  items,
  enumType,
  onClose,
}: KeyValueMenuEditProps) {
  const { mutateAsync: updateKeyValuePairs } =
    useAddUpdateKeyValuePairs(enumType);

  const { control, handleSubmit, reset, register } = useForm<FormValues>({
    defaultValues: { items: items ?? [] },
  });

  useEffect(() => {
    reset({ items: items ?? [] });
  }, [items, reset]);

  const { fields } = useFieldArray({
    control,
    name: "items",
  });

  const submitForm = async (data: FormValues) => {
    const models: AddUpdateKeyValuePairViewModel[] = data.items.map((item) => ({
      Id: item.Id ?? 0,
      Value: item.Value,
      Description: item.Description ?? "",
      PairType: item.PairType ?? 0,
      PairTypeEnumValue: item.PairTypeEnumValue ?? 0,
      BgColorHex: item.BgColorHex ?? "",
      TextColorHex: item.TextColorHex ?? "",
      Status: EntityStatus.Active,
      SortOrder: item.SortOrder ?? 0,
    }));

    await updateKeyValuePairs(models);

    onClose();
  };

  return (
    items &&
    items.length > 0 && (
      <Form onSubmit={handleSubmit(submitForm)}>
        <div className="flex space-y-2">
          <div className="grid grid-cols-1 gap-2">
            {fields.map((field, index) => (
              <div key={field.id}>
                {/* keep the item key (id) in a hidden input so we can send it back */}
                <input
                  type="hidden"
                  {...register(`items.${index}.Id` as const)}
                  defaultValue={field.Id}
                />

                <div className="flex text-sm items-center">
                  <div className="">
                    <Controller
                      control={control}
                      name={`items.${index}.BgColorHex` as const}
                      defaultValue={field.BgColorHex}
                      render={({ field: f }) => (
                        <Field.Root name={`items.${index}.BgColorHex`}>
                          <Field.Control
                            placeholder="BG color hex"
                            type="color"
                            value={f.value}
                            className={`w-7 border-gray-200 p-0.5`}
                            onBlur={f.onBlur}
                            onValueChange={f.onChange}
                            ref={f.ref}
                          />
                        </Field.Root>
                      )}
                    />
                  </div>
                  <div className="grow">
                    <Controller
                      control={control}
                      name={`items.${index}.TextColorHex` as const}
                      defaultValue={field.TextColorHex}
                      render={({ field: f }) => (
                        <Field.Root name={`items.${index}.TextColorHex`}>
                          <Field.Control
                            placeholder="Text color hex"
                            type="color"
                            value={f.value}
                            className={`w-7 border-gray-200 p-0.5`}
                            onBlur={f.onBlur}
                            onValueChange={f.onChange}
                            ref={f.ref}
                          />
                        </Field.Root>
                      )}
                    />
                  </div>
                  <div className="">
                    <Controller
                      control={control}
                      name={`items.${index}.Value` as const}
                      defaultValue={field.Value}
                      render={({
                        field: { value, onChange, onBlur, ref },
                        fieldState: { invalid, isTouched, isDirty, error },
                      }) => (
                        <Field.Root
                          name={`items.${index}.value`}
                          invalid={!!invalid}
                          touched={isTouched}
                          dirty={isDirty}
                        >
                          <Field.Control
                            placeholder="Value (e.g. alice132)"
                            value={value}
                            className="w-30 h-8 rounded border border-gray-200 pl-1 text-gray-600 focus:outline-1 focus:-outline-offset-1 focus:outline-blue-300"
                            onBlur={onBlur}
                            onValueChange={onChange}
                            ref={ref}
                          />
                          <Field.Error match={!!error}>
                            {error?.message}
                          </Field.Error>
                        </Field.Root>
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-between items-center mt-4">
              <Button
                type="submit"
                size="sm"
                variant="ghost"
                className="px-4 py-2 text-gray-600 rounded-sm"
              >
                Apply
              </Button>

              <Button
                size="sm"
                variant="ghost"
                className="px-4 py-2 text-gray-600 rounded-sm"
                onClick={() => onClose()}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Form>
    )
  );
}
